import { useEffect, useMemo, useReducer } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';
import type { AnalyzePrintingsRow, FailureBucket, FilterState, PrintingStatus } from './types';
import { AnalyzePrintingsFilters } from './AnalyzePrintingsFilters';
import { deriveFilterOptions, NO_SYSTEM_SENTINEL } from './deriveFilterOptions';
import { classifyPrinting } from './classifyPrinting';
import { bucketFailure } from './bucketFailure';
import { filterRowsByChart, type ChartFilter } from './filterRowsByChart';
import { PrintingStatusPieChart, type StatusSliceName } from './PrintingStatusPieChart';
import { FailureCausesPieChart } from './FailureCausesPieChart';
import { PrintingsTable } from './PrintingsTable';

const today = () => new Date().toISOString().slice(0, 10);
const yesterday = () => new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

const initialFilter: FilterState = {
  from: yesterday(),
  to: today(),
  machineId: null,
  systemId: null,
  excludedSystemNames: [],
  systemUserId: null,
};

type Action =
  | { type: 'setFilter'; filter: FilterState }
  | { type: 'setRows'; rows: AnalyzePrintingsRow[] }
  | { type: 'setChartFilter'; chartFilter: ChartFilter }
  | {
      type: 'patchRow';
      id: number;
      failureCause: { id: number; name: string } | null;
    };

interface State {
  filter: FilterState;
  rows: AnalyzePrintingsRow[];
  chartFilter: ChartFilter;
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'setFilter':
      // Reset transient drill-down when the date range or other filters change.
      return { ...state, filter: action.filter, chartFilter: null };
    case 'setRows':
      return { ...state, rows: action.rows, chartFilter: null };
    case 'setChartFilter':
      return { ...state, chartFilter: action.chartFilter };
    case 'patchRow':
      return {
        ...state,
        rows: state.rows.map((row) =>
          row.id === action.id
            ? {
                ...row,
                failureCauseId: action.failureCause?.id ?? null,
                failureCause: action.failureCause,
              }
            : row
        ),
      };
  }
};

const sameStatusFilter = (a: ChartFilter, name: StatusSliceName) =>
  a?.kind === 'status' && a.value === (name === 'Success' ? 'success' : 'failure');

const sameBucketFilter = (a: ChartFilter, name: FailureBucket) =>
  a?.kind === 'bucket' && a.value === name;

export const AnalyzePrintings = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [state, dispatch] = useReducer(reducer, {
    filter: initialFilter,
    rows: [],
    chartFilter: null,
  });

  useEffect(() => {
    let cancelled = false;
    const { from, to, machineId, systemId, systemUserId, excludedSystemNames } = state.filter;

    // The "N/A" entry in the Excluded systems dropdown is a UI sentinel that
    // translates to `excludeNoSystem: true` on the backend. Strip it from
    // the array of real system names before sending.
    const excludeNoSystem = excludedSystemNames.includes(NO_SYSTEM_SENTINEL);
    const realExcludedNames = excludeNoSystem
      ? excludedSystemNames.filter((n) => n !== NO_SYSTEM_SENTINEL)
      : excludedSystemNames;

    dataProvider
      .getList<AnalyzePrintingsRow>('analyzePrintings', {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: 'startTime', order: 'DESC' },
        filter: {
          from: `${from}T00:00:00.000Z`,
          to: `${to}T23:59:59.999Z`,
          ...(machineId !== null ? { machineId } : {}),
          ...(systemId !== null ? { systemId } : {}),
          ...(systemUserId !== null ? { systemUserId } : {}),
          ...(realExcludedNames.length ? { excludedSystemNames: realExcludedNames } : {}),
          ...(excludeNoSystem ? { excludeNoSystem: true } : {}),
        },
      })
      .then(({ data }) => {
        if (!cancelled) dispatch({ type: 'setRows', rows: data });
      })
      .catch((err: Error) => {
        // eslint-disable-next-line no-console
        console.error(err);
        if (!cancelled) {
          notify(`Error loading analyze printings: ${err.message}`, { type: 'error' });
          dispatch({ type: 'setRows', rows: [] });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dataProvider, notify, state.filter]);

  const options = useMemo(() => deriveFilterOptions(state.rows), [state.rows]);

  // Donut counts include only `success` and `failure` printings, mirroring
  // legacy hardware-digester behaviour (legacy tracks currently-running
  // printings in `printingsNotUsed` and excludes them from both donuts).
  // `in-progress` rows still appear in the table when no chart filter is
  // active, but get filtered out when the user drills into a slice — same
  // as legacy.
  const { successCount, failureCount, bucketCounts } = useMemo(() => {
    let successCount = 0;
    let failureCount = 0;
    const bucketCounts: Record<FailureBucket, number> = {
      'Filament Deviation Error': 0,
      'Load Cell Error': 0,
      'Manual Stop': 0,
      'Filament Runout': 0,
      'Several Errors': 0,
      'Unfinished Printings': 0,
    };
    for (const r of state.rows) {
      const s: PrintingStatus = classifyPrinting(r);
      if (s === 'success') successCount += 1;
      else if (s === 'failure') {
        failureCount += 1;
        bucketCounts[bucketFailure(r)] += 1;
      }
    }
    return { successCount, failureCount, bucketCounts };
  }, [state.rows]);

  const tableRows = useMemo(
    () => filterRowsByChart(state.rows, state.chartFilter),
    [state.rows, state.chartFilter]
  );

  const statusSelected: StatusSliceName | null =
    state.chartFilter?.kind === 'status'
      ? state.chartFilter.value === 'success'
        ? 'Success'
        : 'Failure'
      : null;

  const bucketSelected: FailureBucket | null =
    state.chartFilter?.kind === 'bucket' ? state.chartFilter.value : null;

  const onStatusSliceClick = (name: StatusSliceName) => {
    if (sameStatusFilter(state.chartFilter, name)) {
      dispatch({ type: 'setChartFilter', chartFilter: null });
      return;
    }
    dispatch({
      type: 'setChartFilter',
      chartFilter: { kind: 'status', value: name === 'Success' ? 'success' : 'failure' },
    });
  };

  const onBucketSliceClick = (name: FailureBucket) => {
    if (sameBucketFilter(state.chartFilter, name)) {
      dispatch({ type: 'setChartFilter', chartFilter: null });
      return;
    }
    dispatch({ type: 'setChartFilter', chartFilter: { kind: 'bucket', value: name } });
  };

  return (
    <Box p={2}>
      <Typography variant="h5" mb={2}>
        Analyze Printings
      </Typography>
      <AnalyzePrintingsFilters
        value={state.filter}
        options={options}
        initial={initialFilter}
        onChange={(filter) => dispatch({ type: 'setFilter', filter })}
      />
      <Typography variant="h6" mt={3} mb={1}>
        Graphs
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" color="text.secondary" mb={1}>
            Printings
          </Typography>
          <PrintingStatusPieChart
            successCount={successCount}
            failureCount={failureCount}
            selected={statusSelected}
            onSliceClick={onStatusSliceClick}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" color="text.secondary" mb={1}>
            Failure Causes
          </Typography>
          <FailureCausesPieChart
            counts={bucketCounts}
            selected={bucketSelected}
            onSliceClick={onBucketSliceClick}
          />
        </Grid>
      </Grid>
      <Box mt={4}>
        <PrintingsTable
          rows={tableRows}
          onPrintingChanged={(id, failureCause) => dispatch({ type: 'patchRow', id, failureCause })}
        />
      </Box>
    </Box>
  );
};
